// ============================================================================
// Shadow Run / Replay Mode — Phase 5 spec 22.0
// ============================================================================
// Save SSE event stream saat run sukses → replay deterministik kalau live gagal.
// Fallback chain saat demo:
//   1. Try /api/osint-stream live SSE
//   2. Jika connect fail dalam 5s atau pipeline.error → load shadow + replay
//   3. Jika tidak ada shadow → load osint_findings cached dari Supabase
//
// Storage: sessionStorage[`horus_osint_shadow_${nik}`] = {
//   events: [{ ts: relativeMs, event: 'platform.start', data: {...} }, ...],
//   savedAt: ISO,
//   subject: { nik, nama_lengkap }
// }
// ============================================================================

const SHADOW_KEY = (nik) => `horus_osint_shadow_${nik}`;

export function recordShadowEvent(nik, event, data, startedAt) {
  if (!nik || !startedAt) return;
  try {
    const key = SHADOW_KEY(nik);
    const raw = sessionStorage.getItem(key);
    const buf = raw ? JSON.parse(raw) : { events: [], savedAt: null };
    buf.events.push({
      ts:    Date.now() - startedAt,
      event,
      data,
    });
    sessionStorage.setItem(key, JSON.stringify(buf));
  } catch {}
}

export function finalizeShadow(nik, subject) {
  if (!nik) return;
  try {
    const key = SHADOW_KEY(nik);
    const raw = sessionStorage.getItem(key);
    if (!raw) return;
    const buf = JSON.parse(raw);
    buf.savedAt = new Date().toISOString();
    buf.subject = { nik, nama_lengkap: subject?.nama_lengkap };
    sessionStorage.setItem(key, JSON.stringify(buf));
  } catch {}
}

export function hasShadow(nik) {
  if (!nik) return false;
  try {
    const raw = sessionStorage.getItem(SHADOW_KEY(nik));
    if (!raw) return false;
    const buf = JSON.parse(raw);
    return Array.isArray(buf?.events) && buf.events.length > 0 && !!buf.savedAt;
  } catch { return false; }
}

export function loadShadow(nik) {
  try {
    const raw = sessionStorage.getItem(SHADOW_KEY(nik));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function clearShadow(nik) {
  try { sessionStorage.removeItem(SHADOW_KEY(nik)); } catch {}
}

// Replay events dengan timing realistis (sama seperti live run).
// onEvent dipanggil persis seperti SSE stream consumer.
// Returns Promise yang resolve setelah pipeline.complete event.
export async function replayShadow(nik, onEvent, opts = {}) {
  const { speedFactor = 1.0 } = opts; // 1.0 = realistic, 2.0 = 2x lebih cepat
  const buf = loadShadow(nik);
  if (!buf?.events?.length) return false;

  const startTs = Date.now();
  for (const e of buf.events) {
    const targetTs = startTs + (e.ts / speedFactor);
    const wait = Math.max(0, targetTs - Date.now());
    if (wait > 0) await new Promise((r) => setTimeout(r, wait));
    try { onEvent(e.event, e.data); } catch {}
  }
  return true;
}
